import { useCallback, useEffect, useRef, useState } from "react";

// Debounced autosave for a draft that is always sent whole.
//   const autosave = useAutosave(saveFn, 2000);
//   autosave.schedule(nextDraft)   // on every change
//   await autosave.flush()         // before submitting; resolves to true when saved
// status: "idle" | "pending" | "saving" | "saved" | "error"
// A failed save keeps the draft queued, so the next change or flush retries it.
export const useAutosave = (save, delay = 2000) => {
  const [status, setStatus] = useState("idle");
  const saveRef = useRef(save);
  const queued = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    saveRef.current = save;
  }, [save]);

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    const draft = queued.current;
    if (draft === null) return true;

    queued.current = null;
    setStatus("saving");
    try {
      await saveRef.current(draft);
      if (queued.current === null) setStatus("saved");
      return true;
    } catch {
      queued.current = queued.current ?? draft;
      setStatus("error");
      return false;
    }
  }, []);

  const schedule = useCallback(
    (draft) => {
      queued.current = draft;
      setStatus("pending");
      clearTimeout(timer.current);
      timer.current = setTimeout(flush, delay);
    },
    [flush, delay],
  );

  // Save anything still queued when leaving the page.
  useEffect(
    () => () => {
      clearTimeout(timer.current);
      if (queued.current !== null)
        saveRef.current(queued.current).catch(() => {});
    },
    [],
  );

  return { status, schedule, flush };
};
